import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AuthLayout from '../../../components/auth/AuthLayout';
import { useSignup } from '../../../contexts/SignupContext';
import LocationPickerMap from '../../../components/common/LocationPickerMap';

const USER_TYPES = [
  'super_distributor',
  'master_distributor',
  'distributor',
  'retailer',
  'api_partner',
];

const initialState = {
  userType: '',
  category: '',
  shopName: '',
  shopNumber: '',
  city: '',
  state: '',
  pincode: '',
  email: '',
  latitude: '',
  longitude: '',
  gst: '',
};

const SignupBusinessDetails = () => {
  const navigate = useNavigate();
  const { updateSignupState } = useSignup();
  const [formData, setFormData] = useState(initialState);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const setCoordinates = (latitude, longitude) => {
      setFormData((prev) => ({
        ...prev,
        latitude: latitude?.toString() || prev.latitude || '0',
        longitude: longitude?.toString() || prev.longitude || '0',
      }));
    };

    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setCoordinates();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setCoordinates(coords?.latitude, coords?.longitude);
      },
      () => {
        setCoordinates();
      },
      { enableHighAccuracy: true }
    );
  }, []);

  const renderLabel = (text, { required = false, optional = false } = {}) => (
    <span>
      {text}
      {required && (
        <span style={{ color: 'red', marginLeft: 4 }} aria-hidden="true">
          *
        </span>
      )}
      {optional && (
        <span style={{ color: '#6b7280', marginLeft: 4 }}>(Optional)</span>
      )}
    </span>
  );

  const handleChange = (event) => {
    const { name, value } = event.target;
    if (name === 'pincode') {
      const formatted = value.replace(/\D/g, '');
      setFormData((prev) => ({ ...prev, [name]: formatted }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const formatUserTypeLabel = (type) =>
    type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

  const handleLocationSelect = useCallback(({ latitude, longitude, city, state, pincode }) => {
    setFormData((prev) => ({
      ...prev,
      latitude: latitude || prev.latitude,
      longitude: longitude || prev.longitude,
      city: city || prev.city,
      state: state || prev.state,
      pincode: (pincode || prev.pincode || '').replace(/\D/g, ''),
    }));
  }, []);

  const handleSubmit = (event) => {
    event.preventDefault();
    setLoading(true);

    updateSignupState({
      businessDetails: formData,
    });

    toast.success('Business details saved');
    navigate('/signup/business-documents');
    setLoading(false);
  };

  return (
    <AuthLayout
      title="Business details"
      subtitle="Tell us about your business"
      onBack={() => navigate(-1)}
    >
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label className="form-label">
            {renderLabel('User Type', { required: true })}
          </label>
          <select
            className="form-input"
            name="userType"
            value={formData.userType}
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Select User Type
            </option>
            {USER_TYPES.map((type) => (
              <option key={type} value={type}>
                {formatUserTypeLabel(type)}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('Category', { required: true })}
          </label>
          <select
            className="form-input"
            name="category"
            value={formData.category}
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Select Category
            </option>
            <option value="mobile_shop">Mobile Shop</option>
            <option value="general_store">General Store</option>
            <option value="kirana">Kirana</option>
            <option value="jewelry_shop">Jewellery Shop</option>
            <option value="electronics_store">Electronics Store</option>
            <option value="pharmacy">Pharmacy</option>
            <option value="others">Others</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('Shop Name', { required: true })}
          </label>
          <input
            type="text"
            className="form-input"
            name="shopName"
            value={formData.shopName}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('Shop Number', { required: true })}
          </label>
          <input
            type="text"
            className="form-input"
            name="shopNumber"
            value={formData.shopNumber}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group mb-4">
          <LocationPickerMap
            latitude={formData.latitude}
            longitude={formData.longitude}
            onLocationSelect={handleLocationSelect}
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('City', { required: true })}
          </label>
          <input
            type="text"
            className="form-input"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('State', { required: true })}
          </label>
          <input
            type="text"
            className="form-input"
            name="state"
            value={formData.state}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('Pincode', { required: true })}
          </label>
          <input
            type="text"
            className="form-input"
            name="pincode"
            value={formData.pincode}
            onChange={handleChange}
            maxLength={6}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('Email', { required: true })}
          </label>
          <input
            type="email"
            className="form-input"
            name="email"
            value={formData.email}
            onChange={handleChange}
            required
          />
        </div>

        <div className="form-group">
          <label className="form-label">
            {renderLabel('GST Number', { optional: true })}
          </label>
          <input
            type="text"
            className="form-input"
            name="gst"
            value={formData.gst}
            onChange={handleChange}
          />
        </div>

        <input type="hidden" name="latitude" value={formData.latitude} />
        <input type="hidden" name="longitude" value={formData.longitude} />

        <button type="submit" className="signin-btn" disabled={loading}>
          {loading ? 'Submitting...' : 'Save & Continue'}
        </button>
      </form>
    </AuthLayout>
  );
};

export default SignupBusinessDetails;


